import { noRefetchDefaults } from "$root/third-party-integrations/react-query/hooks";
import { ProgressBar } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, renderNodeOrFn, useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import {
	type QueriesOptions,
	type QueriesResults,
	type QueryKey,
	type QueryObserverResult,
	type UseQueryOptions,
	type UseQueryResult,
	useQueries,
	useQuery,
} from "@tanstack/react-query";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

export type NonUndefined<T> = T extends undefined ? never : T;

export type ReactQueryWrapperProps<
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
> = {
	/** @default 'noRefetch' */
	mode?: "noRefetch" | "auto";
	children(queryResult: NonUndefined<TData>, query: UseQueryResult<TData, TError>): React.ReactNode;
	loadingFallback?: React.ReactNode;
	errorFallback?: NodeOrFn<{ error: TError; retry: () => Promise<void> }>;
	/**
	 * @default data !== undefined
	 */
	keepChildren?: boolean;
} & UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;

export const DefaultErrorFallback: React.FC<{ retry: () => Promise<void>; text?: string }> = ({ retry, text }) => {
	const { t } = useTranslation();
	return (
		<div className="inline-flex items-center">
			<div className="mr-4">{text ?? t("SOMETHING_WENT_WRONG")}</div>
			<div>
				<Button
					onClickAsync={() => retry()}
					style={{ minWidth: 0, borderRadius: 9999 }}
					palette="secondary"
					size="small"
				>
					{t("RETRY")}
				</Button>
			</div>
		</div>
	);
};

export function customizeErrorFallback(text: string) {
	return function CustomizedErrorFallback({ retry }: { retry: () => Promise<void> }): React.JSX.Element {
		return <DefaultErrorFallback retry={retry} text={text} />;
	};
}

export default function ReactQueryWrapper<
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
>({
	children,
	mode,
	errorFallback,
	loadingFallback,
	keepChildren,
	...useQueryParams
}: ReactQueryWrapperProps<TQueryFnData, TError, TData, TQueryKey>): React.JSX.Element {
	const query = useQuery({
		...(mode === "auto"
			? {}
			: {
					...noRefetchDefaults,
				}),
		...useQueryParams,
	});

	return (
		<ReactQueryWrapperBase
			query={query}
			errorFallback={errorFallback}
			loadingFallback={loadingFallback}
			keepChildren={keepChildren}
		>
			{children}
		</ReactQueryWrapperBase>
	);
}

export type ReactQueryWrapperBaseProps<TData = unknown, TError = unknown> = {
	loadingFallback?: React.ReactNode;
	errorFallback?: NodeOrFn<{ error: TError; retry: () => Promise<void> }>;
	/**
	 * @default query.data !== undefined
	 */
	keepChildren?: boolean;
} & (
	| {
			query: UseQueryResult<TData, TError>;
			children(queryResult: NonUndefined<TData>, query: UseQueryResult<TData, TError>): React.ReactNode;
	  }
	| {
			query: QueryObserverResult<TData, TError>;
			children(queryResult: NonUndefined<TData>, query: QueryObserverResult<TData, TError>): React.ReactNode;
	  }
);

export function ReactQueryWrapperBase<TData = unknown, TError = unknown>(
	props: ReactQueryWrapperBaseProps<TData, TError>,
): React.JSX.Element {
	const queryRef = useUnsafeUpdatedRef(props.query);
	const retry = useCallback(async () => {
		await queryRef.current.refetch();
	}, [queryRef]);
	const errorFallback = props.errorFallback ?? <DefaultErrorFallback retry={retry} />;

	const keepChildren = props.keepChildren ?? props.query.data !== undefined;
	const domId = useUniqueDOMId();

	return (
		<React.Fragment key={domId}>
			{props.query.isError && !(props.query.data !== undefined && keepChildren) ? (
				renderNodeOrFn(errorFallback, {
					retry,
					error: props.query.error,
				})
			) : props.query.isLoading || (props.query.data === undefined && keepChildren) ? (
				typeof props.loadingFallback === "undefined" ? (
					<ProgressBar value="indeterminate" />
				) : (
					props.loadingFallback
				)
			) : (
				props.children(props.query.data as NonUndefined<TData>, props.query)
			)}
		</React.Fragment>
	);
}

export type ReactQueriesWrapperProps<T extends any[]> = {
	queries: readonly [...QueriesOptions<T>];
	context?: UseQueryOptions["context"];

	/** @default 'noRefetch' */
	mode?: "noRefetch" | "auto";
	children(
		queryResults: { [K in keyof T]: NonUndefined<T[K]["data"]> },
		queries: readonly [...QueriesOptions<T>],
	): React.ReactNode;
	loadingFallback?: React.ReactNode;
	errorFallback?: NodeOrFn<{ errors: unknown[]; retry: () => Promise<void> }>;
	/**
	 * @default queries.every(q => q.data !== undefined)
	 */
	keepChildren?: boolean;
};

export function ReactQueriesWrapper<T extends any[]>({
	children,
	mode,
	errorFallback,
	loadingFallback,
	keepChildren,
	queries,
	...useQueriesParams
}: ReactQueriesWrapperProps<T>): React.JSX.Element {
	const q = useQueries({
		...useQueriesParams,
		queries: queries.map((query) => ({
			...(mode === "auto"
				? {}
				: {
						...noRefetchDefaults,
					}),
			...query,
		})),
	}) as QueriesResults<T>;

	return (
		<ReactQueriesWrapperBase
			queries={q}
			errorFallback={errorFallback}
			loadingFallback={loadingFallback}
			keepChildren={keepChildren}
		>
			{children as () => React.JSX.Element}
		</ReactQueriesWrapperBase>
	);
}

export type ReactQueriesWrapperBaseProps<
	Queries extends
		| [UseQueryResult<unknown, unknown>, ...UseQueryResult<unknown, unknown>[]]
		| UseQueryResult<unknown, unknown>[]
		| [],
> = {
	queries: Queries;

	children(queryResults: { [K in keyof Queries]: NonUndefined<Queries[K]["data"]> }, queries: Queries): React.ReactNode;
	loadingFallback?: React.ReactNode;
	errorFallback?: NodeOrFn<{ errors: unknown[]; retry: () => Promise<void> }>;
	/**
	 * @default false
	 */
	keepChildren?: boolean;
};

export function ReactQueriesWrapperBase<
	Queries extends
		| [UseQueryResult<unknown, unknown>, ...UseQueryResult<unknown, unknown>[]]
		| UseQueryResult<unknown, unknown>[]
		| [],
>(props: ReactQueriesWrapperBaseProps<Queries>): React.JSX.Element {
	const queriesRef = useUnsafeUpdatedRef(props.queries);
	const retry = useCallback(async () => {
		await Promise.all(queriesRef.current.filter((q) => q.isError).map((q) => q.refetch()));
	}, [queriesRef]);
	const errorFallback = props.errorFallback ?? <DefaultErrorFallback retry={retry} />;

	const keepChildren = props.keepChildren ?? props.queries.every((q) => q.data !== undefined);
	const domId = useUniqueDOMId();

	return (
		<React.Fragment key={domId}>
			{props.queries.some((q) => q.isError) ? (
				renderNodeOrFn(errorFallback, {
					retry,
					errors: props.queries.map((q) => q.error),
				})
			) : (props.queries.some((q) => q.isLoading) || props.queries.some((q) => q.data === undefined)) &&
				!keepChildren ? (
				typeof props.loadingFallback === "undefined" ? (
					<ProgressBar value="indeterminate" />
				) : (
					props.loadingFallback
				)
			) : (
				props.children(props.queries.map((q) => q.data) as any, props.queries)
			)}
		</React.Fragment>
	);
}
